import { apiBaseUrl, callApi } from '../config';
import { ICompanyAffiliation } from 'types';

export const getUserProfile = async (id: string) => {
  if (!id) {
    throw new Error('getUserProfile: [id] is required');
  }
  return await callApi(apiBaseUrl + `/user`, 'get', { userID: id });
};

export const activateUserProfile = async (userID: string, contractID: string) => {
  return await callApi(apiBaseUrl + `/user/activate`, 'post', { userID, contractID });
};

export const deactivateUserProfile = async ({
  userID,
  contractId,
  lastPaymentAmount,
  contractEndDate,
  voidAllOutstandingInvoices,
}: {
  userID: string;
  contractId: string;
  lastPaymentAmount: number;
  contractEndDate: string;
  voidAllOutstandingInvoices: boolean;
}) => {
  return await callApi(apiBaseUrl + `/user/deactivate`, 'post', {
    userID,
    contractId,
    lastPaymentAmount,
    contractEndDate,
    voidAllOutstandingInvoices,
  });
};

export const setFirstTimePassword = async (hash: string, email: string, hashType = 'bcrypt') => {
  if (!hash) {
    throw new Error('Password is required');
  }
  if (!email) {
    throw new Error('Email is required');
  }
  return await callApi(apiBaseUrl + `/user/set-first-time-password`, 'post', {
    hash,
    email,
    hashType,
  });
};

export const sendMagicLinkEmail = async (userId: string, requestUri: string) => {
  if (!userId) {
    throw new Error('userId is required');
  }
  if (!requestUri) {
    throw new Error('requestUri is required');
  }
  return await callApi(apiBaseUrl + `/email/login`, 'post', {
    userId,
    requestUri,
  });
};

export const getUserAffiliations = async (userID: string) => {
  const response: Promise<{
    affiliationsByCompany: Record<string, ICompanyAffiliation>;
  }> = await callApi(apiBaseUrl + `/users/${userID}/affiliations`, 'get', {});
  const _response = await response;

  const companyIds = Object.keys(_response.affiliationsByCompany);
  companyIds.forEach((companyId) => {
    const w2Contracts = [],
      contractorContracts = [],
      eorContracts = [];
    _response.affiliationsByCompany[companyId].contracts.forEach((contract) => {
      if (contract.type === 'CONTRACTOR') {
        contractorContracts.push(contract);
      } else if (contract.w2Payroll?.checkEmployeeId) {
        const w2Contract = {
          ...contract,
          type: 'US_EMPLOYEE',
        };
        w2Contracts.push(w2Contract);
      } else {
        const eorContract = {
          ...contract,
          type: 'EMPLOYEE',
        };
        eorContracts.push(eorContract);
      }
    });

    _response.affiliationsByCompany[companyId] = {
      ..._response.affiliationsByCompany[companyId],
      w2Contracts,
      contractorContracts,
      eorContracts,
      contracts: [...contractorContracts, ...eorContracts, ...w2Contracts],
    };
  });

  return _response;
};

export const deleteUSEmployee = async (contractId: string) => {
  if (!contractId) {
    throw new Error('deleteUSEmployee: [contractId] is required');
  }
  return await callApi(apiBaseUrl + `/companies/employees/w2/${contractId}`, 'DELETE', {});
};

/**
 * Updates user profile with date of birth
 * @param userID - The user ID to update
 * @param data - The profile data to update
 */
export const updateUserDateOfBirth = async (userID: string, data: { dateOfBirth: string }) => {
  if (!userID) {
    throw new Error('updateUserDateOfBirth: [userID] is required');
  }
  return await callApi(apiBaseUrl + `/companies/employees/${userID}`, 'patch', data);
};
